import { TableColumnType } from "antd";
import { MilestoneKey } from "../enums/Status";
import {
  CellLink,
  EngagementDataSourceType,
  VotingDataSourceType,
} from "./tableModels";
import { PaginationParams, SortType } from "../types/ScreenerTypes";
import { FlagKey, TaskPriority } from "./workflowModels";
import { CustomDropdownProps } from "../components/visuals/PageFilter/PageFilter";
import { AvatarProps } from "../components/visuals/Avatar";

export type ExposureResponseItem = {
  compass_cid: string;
  portfolio_index_id: string;
  voh: number;
  holding_weight: number;
  holding_date: number; // excel date
  quarter?: string;
};

export type PortfolioExposureData = {
  quarter: string;
  date: string;
  exposure: number;
  weight: number;
};

export type FormattedExposureData = {
  id: string;
  data: PortfolioExposureData[];
};

export type ExposureBarItem = {
  date: string;
  [key: string]: number | string;
};

export type VotingAlignment = {
  managerId: string;
  manager: string;
  alignedPct: number; //ISO2 country codes
  manager_id?: string;
  categories?: string[];
  countries?: string[];
  total_votes_count?: number;
  aligned_votes_count?: number;
};

export interface VotingAlignmentCompany {
  manager: string;
  manager_id: string;
  compass_cid: string;
  categories: string[];
  name: string;
  image: string;
  geo_level_2: string;
  sector_code: string;
  total_votes_count: number;
  aligned_votes_count: number;
  alignedPct: number;
  total_count: number;
}

export type DivergentVoting = {
  proposal_subcategory: string;
  divergent_count: number;
};

export interface EngagementItem {
  milestone_id: string;
  milestone_description: string;
  theme: string;
  sub_theme: string;
  manager_id: string;
  manager_name: string;
  manager_image?: string;
  engagement_type_id: string;
  engagement_type_name: string;
  engagement_id: string;
  engagement_title: CellLink;
  last_action_date: string;
  flagStatus: FlagKey;
  /** Company compass_cid when present on the portfolio engagement payload. */
  company_id?: string;
  company_name?: string;
  company_image?: string;
}

export interface EngagementRecord {
  engagement_record_id: string;
  meta_theme: EngagementType;
  theme: string;
  sub_theme: string;
  company: string;
  /** Joined company display fields from GET_ENGAGEMENT_DATA. */
  company_name?: string;
  company_image?: string;
  engagement_type: string;
  engagement_name: string;
  engagement_obj_desc: string;
  engagement_start: string; // ISO 8601 date string
  proj_obj_completion: string; // ISO 8601 date string
  milestone?: string | null;
  m1_status: string;
  m1_start: string; // ISO 8601 date string
  m1_action: string;
  m2_status: string;
  m2_start: string; // ISO 8601 date string
  m2_action: string;
  m3_status: string;
  m3_start: string; // ISO 8601 date string
  m3_action: string;
  m4_status: string;
  m4_start: string; // ISO 8601 date string
  m4_action: string;
  e1_1_set_expectations: boolean;
  e1_2_regular_calls: boolean;
  e1_3_unilateral_calls: boolean;
  e1_4_unilateral_letter: boolean;
  e2_1_collab_meetings: boolean;
  e2_2_collab_letters: boolean;
  e2_3_challenge_calls: boolean;
  e2_4_public_statement: boolean;
  e2_5_collab_public_statement: boolean;
  e2_6_media_cite: boolean;
  e3_1_vote_against: boolean;
  e3_2_vote_esg_resolutions: boolean;
  e3_3_agm_statement: boolean;
  e3_4_pre_disclose_vote: boolean;
  e3_5_co_file_resolutions: boolean;
  e3_6_seek_board_seat: boolean;
  e3_7_call_egm: boolean;
  e3_8_legal_action: boolean;
  e4_1_divest_labelled_funds: boolean;
  e4_2_underweight_funds: boolean;
  e4_3_engage_index_provider: boolean;
  e5_1_private_divest: boolean;
  e5_2_public_divest: boolean;
  fund_id: string;
  manager_id: string;
  manager_name: string;
  image: string;
  job_id: string;
  added_on: string; // ISO 8601 date string
  flag_status: FlagKey;
  tags?: string[] | null; // Array of tag IDs
}

export interface CustomTag {
  id: string;
  display_name: string;
  color: string;
}

export interface TagInput {
  id?: string;
  display_name?: string;
  color?: string;
}

export interface UpsertEngagementRecordRequest {
  engagement_record_id?: string; // Optional for create, required for update
  company: string;
  manager: string;
  category: EngagementType;
  theme: string;
  subTheme: string;
  objectiveTitle: string;
  objectiveDescription?: string;
  sdgs?: number[];
  startDate: string; // ISO 8601 date string
  endDate?: string; // ISO 8601 date string
  milestone?: string;
  currentStatus: string;
  latestUpdate?: string;
  tags?: TagInput[] | null; // Optional tags (existing or new)
}

export interface UpsertEngagementRecordResponse {
  engagement_record_id: string;
}

export type EngagementScreenData = {
  company: string;
  company_name: string;
  company_image: string;
  manager_id: string[];
  portfolio_index_id: string[];
  completed_milestones: number;
  voh: number;
  engagement_count: number;
  corporate_objective_count: number;
};

export enum EngagementType {
  StrategyRiskCommunication = "Strategy",
  Governance = "Governance",
  Social = "Social",
  Environmental = "Environmental",
}

export enum EngagementCategory {
  Issue = "Corporate Issue",
  Objective = "Corporate Objective",
}

export type Engagement = {
  displayValue: EngagementType;
  key: string;
};

export type Milestone = {
  displayValue: string;
  key: MilestoneKey;
};

export interface ActivityData {
  manager: string;
  [EngagementType.StrategyRiskCommunication]: number;
  [EngagementType.Governance]: number;
  [EngagementType.Social]: number;
  [EngagementType.Environmental]: number;
}

export enum Vote {
  For = "For",
  Against = "Against",
}

export interface VoteAction {
  votingRecordId: string;
  managerName: string;
  vote: Vote;
}

export enum ProposalType {
  RoutineBusiness = "Routine Business",
  NonRoutineBusiness = "Non-Routine Business",
}

export interface Proposal {
  voting_record_id: string;
  proposal_id: string;
  proposal_description: string;
  proposal_type: string;
  management_recommendation: Vote;
  voter_id: string;
  voter_name: string;
  vote_type: Vote;
  flag_status: FlagKey;
  rationale: string;
}

export interface ProposalWithAlignmentVote extends Proposal {
  alignmentVote: Vote;
}

export type VotingRecordRequest = {
  compass_cid: string;
};

export type StewardshipFilterParam = {
  compass_cid?: string[];
  engagement_record_id?: string[];
  fund_id?: string[];
  manager_id?: string[];
  pagination?: PaginationParams;
  voting_manager_reference?: string;
  countryIso2?: string[];
  categories?: string[];
  sort?: SortType;
  include_unengaged?: boolean;
  is_config?: boolean;
};

export type VotingRecordItem = {
  voting_record_id: string;
  proposal_id: string;
  proposal_date: string; // ISO 8601 date string
  proposal_category: string;
  proposal_subcategory: string;
  proposal_code_description: string;
  proposal_text: string;
  is_significant_proposal: boolean;
  is_votable_proposal: boolean;
  proponent: string;
  company: string;
  management_recommendation: Vote;
  vote: Vote;
  rationale: string;
  has_voted: boolean;
  fund_id: string;
  manager_id: string;
  manager_name: string;
  job_id: string;
  added_on: string; // ISO 8601 date string
  flag_status: FlagKey;
};

export type DisplayVotingRecordModalParams = {
  displayVotingRecordModal: (recordId: string) => void;
  recordId: string;
};

export type DataEntry<T = string | number | JSX.Element> = {
  label: string;
  labelType?: "small" | "large";
  value: T;
  isPercentage?: boolean;
};

export interface EngagementColumnType extends TableColumnType<EngagementDataSourceType> {
  hasFilters?: boolean;
}

export interface VotingColumnType extends TableColumnType<VotingDataSourceType> {
  hasFilters?: boolean;
}

export type EngagementByManager = {
  manager_id: string;
  manager_name: string;
  engagement_count: number;
};

export type EngagementByGeo = {
  geo_level_2: string;
  engagement_count: number;
};

export type EngagementByTheme = {
  theme: string;
  engagement_count: number;
};

export enum Alignment {
  Aligned = "Aligned",
  Misaligned = "Misaligned",
}

export interface EngagementDataSource {
  key: string;
  theme: AvatarProps;
  priority: CustomDropdownProps;
  momentum: CustomDropdownProps;
  corporateIssues: number;
  corporateObjectives: number;
  managers: AvatarProps[];
}

export enum EngagementKind {
  CorporateIssue = "Corporate Issue",
  CorporateObjective = "Corporate Objective",
}

export enum Momentum {
  STALLING = "STALLING",
  PROGRESSING = "PROGRESSING",
  UNKNOWN = "UNKNOWN",
}

export enum MomentumDisplay {
  STALLING = "Stalling",
  PROGRESSING = "Progressing",
  UNKNOWN = "Unknown",
}

export type EngagementSubthemeMetric = {
  engagement_subtheme_metric_id: string;
  compass_cid: string;
  sub_theme: string;
  priority: TaskPriority;
  momentum: Momentum;
};

export type CompanyEngagementMateriality = {
  compass_cid: string;
  name: string;
  image: string;
  voh_sum: number;
  total_material_topics: number;
  covered_topics: number;
  coverage: number;
  weight: number;
  total_rows: number;
};

export interface TopicMapping {
  compass_cid: string;
  meta_theme: EngagementType;
  theme: string;
  sub_theme: string;
  is_mapped: boolean;
  sub_sector: string;
  description?: string;
  topic_name?: string;
  topic_id?: string;
}

// Engagement Timeline types
export interface TimelineActivityNote {
  note_id: string;
  title: string;
  content: string;
  created_by: string;
  timestamp: string;
}

export interface TimelineVotingMapping {
  compass_cid: string;
  voting_record_id: string;
  engagement_record_id: string;
  confidence_score: number;
  rationale: string;
  is_cross_manager: boolean;
  matching_factors: string;
  interaction_class: string;
  interaction_class_rationale?: string;
  linkage_type: string;
  linkage_type_rationale?: string;
  sequence_flow: string;
  sequence_flow_rationale?: string;
  run_id: string;
  run_timestamp: string;
}

export interface TimelineActivityExtras {
  milestone?: string;
  momentum?: string;
  interaction_title?: string;
  sdgs?: string[];
  escalations?: string[];
  assignee?: string;
  deadline?: string;
  priority?: string;
  status?: string;
  is_flag?: boolean;
  is_archived?: boolean;
  notes?: TimelineActivityNote[];
  voting_mapping?: TimelineVotingMapping;
}

export interface TimelineActivityResponse {
  id: string;
  type: "INTERACTION" | "TASK" | "VOTE";
  date: string;
  description: string;
  details: string;
  subject_id: string;
  extras: TimelineActivityExtras;
}

export interface EngagementTimelineRequest {
  engagement_record_id: string;
}
